import type { StoredJoinSplitNote } from "../joinsplit/types";
import type { FlatJoinSplitV2NoteSyncResult } from "./sync";

/** A scope is a fresh React memo token, not an address (A -> B -> A must cancel). */
export function createWalletSyncGuard() {
  let currentScope: object | null = null;
  let generation = 0;
  let latestRun = 0;
  let activeRun: number | null = null;
  const setScope = (scope: object | null) => {
    if (currentScope === scope) return;
    currentScope = scope;
    generation++;
    activeRun = null;
  };
  return {
    setScope,
    clearScope(scope: object) {
      if (currentScope === scope) setScope(null);
    },
    capture(scope: object) {
      const capturedGeneration = generation;
      return () => currentScope === scope && capturedGeneration === generation;
    },
    begin(scope: object) {
      if (currentScope !== scope || activeRun !== null) return null;
      const capturedGeneration = generation;
      const run = ++latestRun;
      activeRun = run;
      return {
        isCurrent: () =>
          currentScope === scope &&
          capturedGeneration === generation &&
          latestRun === run,
        finish() {
          // A stale completion must never unlock a newer wallet's operation.
          if (activeRun === run) activeRun = null;
        },
      };
    },
  };
}

/** Called inside React's functional updater, which may execute after a switch. */
export function mergeValidatedFlatInboxNotes(
  current: StoredJoinSplitNote[],
  authoritative: FlatJoinSplitV2NoteSyncResult,
  auditedCommitments: ReadonlySet<string>,
  initialLocalCommitments: ReadonlySet<string>,
  isCurrent: () => boolean,
): StoredJoinSplitNote[] {
  if (!isCurrent()) return current;
  const currentCommitments = new Set(
    current.map((stored) => stored.note.commitment.toLowerCase()),
  );
  const next = new Map<string, StoredJoinSplitNote>();
  for (const local of current) {
    const key = local.note.commitment.toLowerCase();
    // A scan cannot classify concurrent additions outside its input snapshot.
    if (!auditedCommitments.has(key)) {
      next.set(key, local);
    } else if (
      !authoritative.spent.has(key) &&
      !authoritative.rejected.has(key)
    ) {
      next.set(
        key,
        authoritative.validated.get(key) || {
          ...local,
          note: { ...local.note, leafIndex: -1 },
        },
      );
    }
  }
  for (const [key, validated] of authoritative.validated) {
    // Do not resurrect a local note removed by a spend during the scan.
    if (initialLocalCommitments.has(key) && !currentCommitments.has(key)) {
      continue;
    }
    next.set(key, validated);
  }
  return Array.from(next.values());
}
